import { useState } from 'react'
import { Check, Plus, Search } from 'lucide-react'
import { moduleStyle } from '@/components/dashboard/moduleStyle'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { SHIFT_TILES, toggleTile } from '@/lib/shiftTiles'
import { cn } from '@/lib/utils'
import { TILE_MODULE } from './ShiftTiles'

/**
 * The widget gallery for the layout being edited (z8nrz7fbcn), in the Home
 * dashboard's Add widgets style: every shift widget as a card with its module
 * icon, what it shows, and Add / Added. Changes land in the draft; nothing is
 * stored until the layout is saved.
 */
export function AddShiftWidgetsDialog({
  open,
  onOpenChange,
  draft,
  onChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: string[]
  onChange: (ids: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const shown = SHIFT_TILES.filter(
    (t) => !q || t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q),
  )
  const added = draft.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-0 p-0 sm:max-w-3xl'>
        <DialogHeader className='space-y-1 border-b px-6 py-4 text-left'>
          <DialogTitle>Add widgets</DialogTitle>
          <DialogDescription>
            {added} of {SHIFT_TILES.length} on this view. Saving the layout changes it for everyone.
          </DialogDescription>
        </DialogHeader>

        <div className='flex items-center gap-3 border-b px-6 py-3'>
          <div className='relative flex-1'>
            <Search aria-hidden className='pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400' />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search widgets, e.g. "overdue" or "stock"'
              aria-label='Search widgets'
              className='h-9 pl-9'
              autoFocus
            />
          </div>
          <Button size='sm' onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>

        <ul className='grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto p-6 sm:grid-cols-2 lg:grid-cols-3'>
          {shown.length === 0 && (
            <li className='col-span-full py-8 text-center text-sm text-gray-500'>No widgets match “{query}”.</li>
          )}
          {shown.map((t) => {
            const on = draft.includes(t.id)
            const mod = TILE_MODULE[t.id]
            const style = mod ? moduleStyle(mod.moduleId) : null
            const Icon = style?.icon
            return (
              <li
                key={t.id}
                className={cn(
                  'flex flex-col rounded-xl border bg-white p-4 transition-colors',
                  on ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200',
                )}
              >
                <div className='flex items-start gap-2.5'>
                  {Icon && (
                    <span aria-hidden className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', style.tile)}>
                      <Icon className='h-3.5 w-3.5 text-white' />
                    </span>
                  )}
                  <div className='min-w-0'>
                    <p className='text-sm font-semibold text-gray-800'>{t.title}</p>
                    <p className='mt-0.5 text-xs text-gray-500'>{t.description}</p>
                  </div>
                </div>
                <div className='mt-3 flex items-center justify-between'>
                  <span className='text-[11px] text-gray-400'>{mod?.label}</span>
                  <Button
                    size='sm'
                    variant={on ? 'default' : 'outline'}
                    className='h-7 px-2.5 text-xs'
                    aria-pressed={on}
                    aria-label={on ? `Remove ${t.title}` : `Add ${t.title}`}
                    onClick={() => onChange(toggleTile(draft, t.id, !on))}
                  >
                    {on ? (
                      <>
                        <Check aria-hidden className='mr-1 h-3.5 w-3.5' /> Added
                      </>
                    ) : (
                      <>
                        <Plus aria-hidden className='mr-1 h-3.5 w-3.5' /> Add
                      </>
                    )}
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
